import React, { useState, useEffect } from 'react';
import { EmptyState } from '../../ui';
import SimpleModal from '../../composite/SimpleModal';
import { EnvelopeOpenIcon, ClockIcon } from '@radix-ui/react-icons';
import { apiCall } from '../../../utils';

// Simple time ago function
const getTimeAgo = (dateString) => {
	const date = new Date(dateString);
	const now = new Date();
	const seconds = Math.floor((now - date) / 1000);

	if (seconds < 60) return 'just now';
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
	const months = Math.floor(days / 30);
	if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
	const years = Math.floor(months / 12);
	return `${years} year${years !== 1 ? 's' : ''} ago`;
};

const STATUS_COLORS = {
	open: 'bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300',
	in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300',
	waiting_for_user: 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300',
	resolved: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
	closed: 'bg-muted text-foreground',
};

const PRIORITY_COLORS = {
	low: 'text-muted-foreground',
	medium: 'text-yellow-600',
	high: 'text-orange-600',
	critical: 'text-red-600',
};

const TYPE_LABELS = {
	bug_report: 'Bug Report',
	feature_request: 'Feature Request',
	account_issue: 'Account Issue',
	scan_problem: 'Scan Problem',
	billing: 'Billing',
	general: 'General',
};

export default function SupportTicketList() {
	const [tickets, setTickets] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [filter, setFilter] = useState('all'); // all, open, resolved

	useEffect(() => {
		fetchTickets();
	}, []);

	const fetchTickets = async () => {
		try {
			setLoading(true);
			const response = await apiCall('/api/support/tickets');
			if (response.success) {
				setTickets(response.data);
			} else {
				throw new Error(response.error || 'Failed to fetch tickets');
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const filteredTickets = tickets.filter(ticket => {
		if (filter === 'all') return true;
		if (filter === 'open') return ['open', 'in_progress', 'waiting_for_user'].includes(ticket.status);
		if (filter === 'resolved') return ['resolved', 'closed'].includes(ticket.status);
		return true;
	});

	if (loading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
				{error}
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Filter Tabs */}
			<div className="flex gap-2 border-b">
				<button
					onClick={() => setFilter('all')}
					className={`px-4 py-2 font-medium transition-colors ${filter === 'all'
						? 'text-brand-600 border-b-2 border-brand-600'
						: 'text-muted-foreground hover:text-foreground'
						}`}
				>
					All Tickets ({tickets.length})
				</button>
				<button
					onClick={() => setFilter('open')}
					className={`px-4 py-2 font-medium transition-colors ${filter === 'open'
						? 'text-brand-600 border-b-2 border-brand-600'
						: 'text-muted-foreground hover:text-foreground'
						}`}
				>
					Open ({tickets.filter(t => ['open', 'in_progress', 'waiting_for_user'].includes(t.status)).length})
				</button>
				<button
					onClick={() => setFilter('resolved')}
					className={`px-4 py-2 font-medium transition-colors ${filter === 'resolved'
						? 'text-brand-600 border-b-2 border-brand-600'
						: 'text-muted-foreground hover:text-foreground'
						}`}
				>
					Resolved ({tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length})
				</button>
			</div>

			{/* Tickets List */}
			{filteredTickets.length === 0 ? (
				<EmptyState
					icon={EnvelopeOpenIcon}
					title="No tickets here"
					description="When you raise a ticket it appears here with its status and our replies."
				/>
			) : (
				<div className="space-y-4">
					{filteredTickets.map(ticket => (
						<div
							key={ticket._id}
							className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
							onClick={() => setSelectedTicket(ticket)}
						>
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<div className="flex items-center gap-3 mb-2">
										<h3 className="font-semibold text-foreground">{ticket.title}</h3>
										<span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[ticket.status]}`}>
											{ticket.status.replace('_', ' ')}
										</span>
										<span className={`text-xs font-medium ${PRIORITY_COLORS[ticket.priority]}`}>
											{ticket.priority}
										</span>
									</div>
									<p className="text-sm text-muted-foreground mb-2 line-clamp-2">
										{ticket.description}
									</p>
									<div className="flex items-center gap-4 text-xs text-muted-foreground">
										<span>{TYPE_LABELS[ticket.type]}</span>
										<span>•</span>
										<span>{ticket.ticketId}</span>
										<span>•</span>
										<span className="flex items-center gap-1">
											<ClockIcon className="w-3 h-3" />
											{getTimeAgo(ticket.createdAt)}
										</span>
										{ticket.responses && ticket.responses.length > 0 && (
											<>
												<span>•</span>
												<span>{ticket.responses.length} responses</span>
											</>
										)}
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{/* Ticket Detail Modal would go here */}
			{selectedTicket && (
				<TicketDetailModal
					ticket={selectedTicket}
					onClose={() => setSelectedTicket(null)}
					onUpdate={() => {
						fetchTickets();
						setSelectedTicket(null);
					}}
				/>
			)}
		</div>
	);
}

// Ticket Detail Modal Component
function TicketDetailModal({ ticket, onClose, onUpdate }) {
	const [response, setResponse] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const handleAddResponse = async (e) => {
		e.preventDefault();
		if (!response.trim()) return;

		setLoading(true);
		setError('');

		try {
			const result = await apiCall(`/api/support/tickets/${ticket._id}/responses`, {
				method: 'POST',
				body: JSON.stringify({ message: response }),
			});

			if (result.success) {
				setResponse('');
				onUpdate();
			} else {
				throw new Error(result.error || 'Failed to add response');
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<SimpleModal isOpen onClose={onClose} title={ticket.title} size="2xl">
			<div>
				<div>
					<div className="flex items-center gap-3 text-sm">
						<span className={`px-2 py-1 rounded-full ${STATUS_COLORS[ticket.status]}`}>
							{ticket.status.replace('_', ' ')}
						</span>
						<span className={`font-medium ${PRIORITY_COLORS[ticket.priority]}`}>
							{ticket.priority} priority
						</span>
						<span className="text-muted-foreground">{ticket.ticketId}</span>
					</div>
				</div>

				<div className="p-6 overflow-y-auto max-h-[60vh]">
					{/* Original Message */}
					<div className="mb-6">
						<h3 className="font-medium text-foreground mb-2">Description</h3>
						<p className="text-foreground whitespace-pre-wrap">{ticket.description}</p>
						<div className="mt-3 text-sm text-muted-foreground">
							<p>Type: {TYPE_LABELS[ticket.type]}</p>
							<p>Created: {new Date(ticket.createdAt).toLocaleString()}</p>
						</div>
					</div>

					{/* Responses */}
					{ticket.responses && ticket.responses.length > 0 && (
						<div className="space-y-4 mb-6">
							<h3 className="font-medium text-foreground">Conversation</h3>
							{ticket.responses.map((resp, index) => (
								<div
									key={index}
									className={`p-4 rounded-lg ${resp.from === 'support'
										? 'bg-brand-50 ml-8 dark:bg-brand-500/10'
										: 'bg-muted mr-8'
										}`}
								>
									<div className="flex items-center justify-between mb-2">
										<span className="font-medium text-sm">
											{resp.from === 'support' ? 'Support Team' : 'You'}
										</span>
										<span className="text-xs text-muted-foreground">
											{getTimeAgo(resp.timestamp)}
										</span>
									</div>
									<p className="text-foreground whitespace-pre-wrap">{resp.message}</p>
								</div>
							))}
						</div>
					)}

					{/* Add Response Form */}
					{['open', 'in_progress', 'waiting_for_user'].includes(ticket.status) && (
						<form onSubmit={handleAddResponse} className="border-t pt-4">
							<h3 className="font-medium text-foreground mb-3">Add Response</h3>
							{error && (
								<div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
									{error}
								</div>
							)}
							<textarea
								value={response}
								onChange={(e) => setResponse(e.target.value)}
								placeholder="Type your response here..."
								className="w-full px-3 py-2 border border-input rounded-lg focus:ring-brand-500 focus:border-brand-500"
								rows={4}
								disabled={loading}
							/>
							<div className="flex justify-end mt-3">
								<button
									type="submit"
									disabled={loading || !response.trim()}
									className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{loading ? 'Sending...' : 'Send Response'}
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
		</SimpleModal>
	);
}
